import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { History, RotateCcw, Eye, Check, Loader2 } from 'lucide-react';

interface SiteSketchVersionHistoryProps {
  locationId: string;
  isOpen: boolean;
  onClose: () => void;
  onRestore: (url: string) => void;
}

export function SiteSketchVersionHistory({
  locationId,
  isOpen,
  onClose,
  onRestore
}: SiteSketchVersionHistoryProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const { data: versions = [], isLoading } = useQuery({
    queryKey: ['site-sketch-versions', locationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_sketch_versions')
        .select('*')
        .eq('location_id', locationId)
        .order('version_number', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: isOpen,
  });

  const handleRestore = async (version: any) => {
    setRestoringId(version.id);
    
    try {
      // Update is_current for all versions
      await supabase
        .from('site_sketch_versions')
        .update({ is_current: false })
        .eq('location_id', locationId);

      // Set this version as current
      await supabase
        .from('site_sketch_versions')
        .update({ is_current: true })
        .eq('id', version.id);

      // Update the location's site_sketch_url
      const { error: updateError } = await supabase
        .from('charging_locations')
        .update({ site_sketch_url: version.sketch_url })
        .eq('id', locationId);

      if (updateError) throw updateError;

      toast({
        title: 'Versie hersteld',
        description: `Schets versie ${version.version_number} is nu actief`,
      });

      onRestore(version.sketch_url);
      queryClient.invalidateQueries({ queryKey: ['site-sketch-versions', locationId] });
      onClose();
    } catch (error: any) {
      toast({
        title: 'Fout bij herstellen',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setRestoringId(null);
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Versiegeschiedenis Situatieschets
            </DialogTitle>
          </DialogHeader>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : versions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Geen versies beschikbaar
            </div>
          ) : (
            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-3">
                {versions.map((version: any) => (
                  <div
                    key={version.id}
                    className={`flex items-center gap-4 p-3 rounded-lg border ${
                      version.is_current ? 'border-primary bg-primary/5' : 'border-border'
                    }`}
                  >
                    {/* Thumbnail */}
                    <div 
                      className="w-20 h-20 rounded-md overflow-hidden bg-muted flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => setPreviewUrl(version.sketch_url)}
                    >
                      <img
                        src={version.sketch_url}
                        alt={`Versie ${version.version_number}`}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">Versie {version.version_number}</span>
                        {version.is_current && (
                          <Badge variant="default" className="text-xs">
                            <Check className="w-3 h-3 mr-1" />
                            Actief
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {format(new Date(version.uploaded_at), 'd MMMM yyyy, HH:mm', { locale: nl })}
                      </p>
                      {version.notes && (
                        <p className="text-xs text-muted-foreground mt-1 truncate">{version.notes}</p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setPreviewUrl(version.sketch_url)}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      {!version.is_current && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRestore(version)}
                          disabled={restoringId === version.id}
                        >
                          {restoringId === version.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <RotateCcw className="w-4 h-4 mr-1" />
                              Herstel
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!previewUrl} onOpenChange={() => setPreviewUrl(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Preview</DialogTitle>
          </DialogHeader>
          {previewUrl && (
            <div className="flex items-center justify-center">
              <img
                src={previewUrl}
                alt="Situatieschets preview"
                className="max-w-full max-h-[70vh] object-contain rounded-lg"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
